import clsx from "clsx"

import useHeightCSSVariable from "@hooks/useHeightCSSVariable"

import { faIcon } from "@utils/faIcon"

import { ExtraTouchArea } from "@particles/index"

import { Avatar, BottomDrawer, Divider, Hint, Icon, IconButton } from "@atoms"

import styles from "./DrawerHeader.module.scss"
import type {
    DrawerHeaderIconButtonProps,
    DrawerHeaderLeftSlotProps,
    DrawerHeaderRootProps,
    DrawerHeaderTitleProps,
} from "./DrawerHeader.types"

function DrawerHeaderRoot(props: DrawerHeaderRootProps & { children?: React.ReactNode }) {
    const { className, showDivider = true, showDragIndicator = false, children } = props

    const headerRef = useHeightCSSVariable<HTMLDivElement>("--popover-header-height")

    return (
        <div
            ref={headerRef}
            className={clsx(styles.base, className)}
            data-has-drag-indicator={showDragIndicator || undefined}
        >
            {showDragIndicator && <BottomDrawer.DragIndicator />}
            <div className={styles.innerContainer}>{children}</div>
            {showDivider && <Divider />}
        </div>
    )
}

function DrawerHeaderTitle(props: DrawerHeaderTitleProps) {
    return (
        <div className={clsx(styles.headingWrapper, props.titleClassName)}>
            <div className={styles.titleContainer}>
                {props.avatar && <Avatar {...props.avatar} />}
                <div className={styles.iconAndTitle}>
                    {props.titleIcon && <Icon icon={props.titleIcon} size={14} />}
                    <div className={styles.titleAndSupport}>
                        <div className={styles.title}>
                            {props.title}
                            {props.counter && <div className={styles.counter}>{props.counter}</div>}
                            {props.hint && <Hint {...props.hint} />}
                        </div>
                        {props.supportText && <div className={styles.supportText}>{props.supportText}</div>}
                    </div>
                </div>
            </div>
        </div>
    )
}

function DrawerHeaderLeftSlot(props: DrawerHeaderLeftSlotProps) {
    const { children } = props
    return children
}

function DrawerHeaderLeftButton(props: DrawerHeaderIconButtonProps) {
    return (
        <ExtraTouchArea extraTouchArea={8}>
            <IconButton
                size="sm"
                variant="ghost"
                className={clsx(styles.iconButton, props.className)}
                isTabbable={Boolean(props.isTabbable)}
                icon={props.icon || faIcon({ name: "arrow-left", style: "regular" })}
                onClick={props.onClick}
                aria-label={props["aria-label"]}
                noDebounce={true}
            />
        </ExtraTouchArea>
    )
}

function DrawerHeaderRightButton(props: DrawerHeaderIconButtonProps) {
    return (
        <ExtraTouchArea extraTouchArea={8}>
            <IconButton
                size="sm"
                variant="muted"
                className={clsx(styles.iconButton, props.className)}
                isTabbable={Boolean(props.isTabbable)}
                isPressed={Boolean(props.isPressed)}
                icon={props.icon || faIcon({ name: "xmark", style: "regular" })}
                iconOnActive={props.iconOnActive || faIcon({ name: "xmark", style: "solid" })}
                iconIsActive={Boolean(props.isPressed)}
                onClick={props.onClick}
                aria-label={props["aria-label"] || "Close"}
                noDebounce={true}
            />
        </ExtraTouchArea>
    )
}

export default {
    Root: DrawerHeaderRoot,
    Title: DrawerHeaderTitle,
    LeftSlot: DrawerHeaderLeftSlot,
    LeftButton: DrawerHeaderLeftButton,
    RightButton: DrawerHeaderRightButton,
}
